/**
 * Assistant message segments: the agent's working narration ("steps") kept
 * apart from the answer the user asked for.
 *
 * WIRE CONTRACT (web-backend persistence, single turn browser flow plan §3):
 *
 * - `metadata.segments`: `[{ kind: "steps" | "answer", text }]` in stream
 *   order. Written only when at least one steps segment ever existed, so an
 *   ordinary message carries none and renders exactly as before.
 * - `metadata.browser_continuations`: `[{ action, at, stream_id, partial,
 *   andi_cost? }]`, the audit trail of hand backs and approval answers that
 *   continued this message. There is no user message for them.
 * - `content` holds only the user facing answer text (answers plus the
 *   andi-browser fence), continuation turns joined with a blank line.
 *
 * Streaming: `delta.content` extends (or starts) an answer segment,
 * `delta.reasoning_content` extends (or starts) a steps segment. Segments of a
 * continuation are appended after the message's existing ones, never merged
 * into them, mirroring the server.
 */

import { parseAndiBrowser, type AndiBrowserBlock } from '@/components/chat/browser/types';

export type MessageSegmentKind = 'steps' | 'answer';

export interface MessageSegment {
  kind: MessageSegmentKind;
  text: string;
}

/**
 * `review_continue` is the "Review and continue" card on a scheduled run
 * message whose background browser session parked (vault contract C12.4).
 */
export type BrowserResumeAction = 'handed_back' | 'approved' | 'declined' | 'review_continue';

export interface BrowserContinuationRecord {
  action: BrowserResumeAction;
  at?: string;
  stream_id?: string;
  partial?: boolean;
}

/** Stable empty list, so selectors and memo comparisons see one reference. */
export const EMPTY_SEGMENTS: MessageSegment[] = [];

/**
 * Join two turns of answer text the way the server does:
 * `a + "\n\n" + b` when both are non empty, else whichever one is.
 */
export function joinContent(a: string, b: string): string {
  if (a !== '' && b !== '') return `${a}\n\n${b}`;
  return a !== '' ? a : b;
}

/**
 * Extend the last segment when it has the same kind, otherwise start a new
 * one. Returns a new array; the input is not mutated.
 */
export function appendSegment(
  segments: MessageSegment[],
  kind: MessageSegmentKind,
  text: string
): MessageSegment[] {
  if (text === '') return segments;
  const last = segments[segments.length - 1];
  if (last?.kind === kind) {
    return [...segments.slice(0, -1), { kind, text: last.text + text }];
  }
  return [...segments, { kind, text }];
}

export function hasStepsSegment(segments: readonly MessageSegment[]): boolean {
  return segments.some((segment) => segment.kind === 'steps');
}

/** Read `metadata.segments` defensively; malformed entries are skipped. */
export function readSegments(metadata: Record<string, unknown> | undefined): MessageSegment[] {
  const raw = metadata?.segments;
  if (!Array.isArray(raw)) return EMPTY_SEGMENTS;
  const out: MessageSegment[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { kind, text } = entry as Record<string, unknown>;
    if ((kind === 'steps' || kind === 'answer') && typeof text === 'string') {
      out.push({ kind, text });
    }
  }
  return out;
}

/** Read `metadata.browser_continuations` defensively. */
export function readContinuations(
  metadata: Record<string, unknown> | undefined
): BrowserContinuationRecord[] {
  const raw = metadata?.browser_continuations;
  if (!Array.isArray(raw)) return [];
  const out: BrowserContinuationRecord[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const action = record.action;
    if (
      action !== 'handed_back' &&
      action !== 'approved' &&
      action !== 'declined' &&
      action !== 'review_continue'
    )
      continue;
    out.push({
      action,
      ...(typeof record.at === 'string' ? { at: record.at } : {}),
      ...(typeof record.stream_id === 'string' ? { stream_id: record.stream_id } : {}),
      ...(typeof record.partial === 'boolean' ? { partial: record.partial } : {}),
    });
  }
  return out;
}

/**
 * The segments a message already has, for a continuation to append to. A
 * message without segments is seeded with its content as one answer segment,
 * exactly as the server seeds it.
 */
export function baseSegmentsOf(
  content: string,
  metadata: Record<string, unknown>
): MessageSegment[] {
  const existing = readSegments(metadata);
  if (existing.length > 0) return existing;
  return content !== '' ? [{ kind: 'answer', text: content }] : [];
}

/**
 * Split segments into what goes behind the Steps disclosure and the final
 * answer: the trailing run of answer segments is the final answer, and
 * everything before it (steps and earlier answers) is history.
 */
export function partitionSegments(segments: readonly MessageSegment[]): {
  earlier: MessageSegment[];
  final: MessageSegment[];
} {
  let split = segments.length;
  while (split > 0 && segments[split - 1]?.kind === 'answer') split -= 1;
  return { earlier: segments.slice(0, split), final: segments.slice(split) };
}

/**
 * A complete andi-browser fence. Mirrors the orchestrator's `_FENCE_RE`
 * (browsing/session_owner.py) so both sides agree on what a fence is.
 */
const FENCE_RE = /```andi-browser[ \t]*\r?\n([\s\S]*?)\r?\n```/g;
/** An andi-browser fence whose closing marker has not streamed in yet. */
const OPEN_FENCE_RE = /```andi-browser[\s\S]*$/;

/** Every well formed andi-browser block in the text, in order. */
export function extractBrowserBlocks(text: string): AndiBrowserBlock[] {
  const blocks: AndiBrowserBlock[] = [];
  for (const match of text.matchAll(FENCE_RE)) {
    const block = parseAndiBrowser(match[1] ?? '');
    if (block) blocks.push(block);
  }
  return blocks;
}

const BLANK_LINE = '\n\n';

/**
 * Only the complete andi-browser fences of a text, joined by blank lines, or
 * '' when there is none. Stable while a reply streams on past its fences, so
 * a selector can return it without changing on every chunk.
 */
export function browserFenceText(text: string | null | undefined): string {
  if (!text || !text.includes('```andi-browser')) return '';
  return Array.from(text.matchAll(FENCE_RE), (match) => match[0]).join(BLANK_LINE);
}

/**
 * Remove andi-browser fences (and a trailing unfinished one) from markdown,
 * because the segmented layout renders the card in its own slot.
 */
export function stripBrowserFences(text: string): string {
  return text.replace(FENCE_RE, '').replace(OPEN_FENCE_RE, '').trim();
}

/** One card per unique session handle across all segments, first seen first. */
export function uniqueBrowserBlocks(segments: readonly MessageSegment[]): AndiBrowserBlock[] {
  const seen = new Set<string>();
  const out: AndiBrowserBlock[] = [];
  for (const segment of segments) {
    for (const block of extractBrowserBlocks(segment.text)) {
      if (seen.has(block.sessionHandle)) continue;
      seen.add(block.sessionHandle);
      out.push(block);
    }
  }
  return out;
}

/** Whether a message's content carries an andi-browser fence for this handle. */
export function contentHasBrowserHandle(content: string, handle: string): boolean {
  return extractBrowserBlocks(content).some((block) => block.sessionHandle === handle);
}

/** A card web-backend closed without a continuation. "Continued" is not one. */
export type BrowserReviewClosedState = 'skipped' | 'superseded';

/**
 * A plain lowercase registrable domain. Mirrors web-backend's
 * `PLAIN_DOMAIN_RE` (browser_resume.py): the client checks again because a
 * stored value is not trusted, and page text must never reach the card.
 */
const PLAIN_DOMAIN_RE = /^(?=.{1,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/**
 * `metadata.browser_review` on a scheduled run message whose background
 * browser session parked (vault contract C12.4). Rendered by
 * BrowserReviewCard.
 */
export interface BrowserReview {
  /** Plain lowercase domain of the parked session, '' when absent or not plain. */
  domain: string;
  /** `kind === "human_check"` exactly: selects the human check copy. */
  humanCheck: boolean;
  /** `cart === true` exactly: the run carried the cart add grant. */
  cart: boolean;
  /** `state` when web-backend closed the card, null while it is open. */
  state: BrowserReviewClosedState | null;
}

/**
 * Read `metadata.browser_review` defensively. Null unless well formed. Its
 * `message` (English, written by web-backend) is deliberately not read: the
 * card builds its own sentence in the user's language (L5). Every key added
 * after `v: 1` shipped (`kind`, `cart`, `state`; `park_class` is not read, only `kind` picks the copy) is optional and
 * additive: a missing, mistyped or unknown value reads as the generic, open
 * card and never throws.
 */
export function readBrowserReview(
  metadata: Record<string, unknown> | undefined
): BrowserReview | null {
  const raw = metadata?.browser_review;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const review = raw as Record<string, unknown>;
  if (review.action !== 'review_continue') return null;
  const domain =
    typeof review.domain === 'string' && PLAIN_DOMAIN_RE.test(review.domain) ? review.domain : '';
  const state = review.state === 'skipped' || review.state === 'superseded' ? review.state : null;
  return {
    domain,
    humanCheck: review.kind === 'human_check',
    cart: review.cart === true,
    state,
  };
}
